import React, { useEffect, useState, useCallback } from "react";
import { apiFetch } from "./lib/apiFetch";
import useIdleTimeout from "./hooks/useIdleTimeout";
import SessionTimeoutDialog from "./components/SessionTimeoutDialog";
import { Routes, Route } from 'react-router-dom';
import Login from './components/Login';
import ProtectedRoute from './components/ProtectedRoute';
import BaseLayout from './components/BaseLayout';
import Home from './pages/Home';
import TrainingSetList from './pages/TrainingSetList';
import TrainingSetDetail from './pages/TrainingSetDetail';
import JobDashboard from './pages/JobDashboard';
import JobDetail from './pages/JobDetail';
import DocumentModels from './pages/DocumentModels';
import NotificationPage from './pages/NotificationPage';
import FileUpload from './components/FileUpload';
import SecurityAdminLayout from './pages/SecurityAdminLayout';
import SecurityUsers from './pages/SecurityUsers';
import SecurityUserDetail from './pages/SecurityUserDetail';
import SecurityGroups from './pages/SecurityGroups';
import SecurityGroupDetail from './pages/SecurityGroupDetail';
import SecurityRoles from './pages/SecurityRoles';
import SecurityRoleDetail from './pages/SecurityRoleDetail';
import SecurityFeatures from './pages/SecurityFeatures';
import SecuritySettings from './pages/SecuritySettings';
import ChangePassword from './pages/ChangePassword';
import Account from './pages/Account';
import { AuthProvider } from './contexts/AuthContext';
import TermsOfUse from './pages/TermsOfUse';
import PrivacyPolicy from './pages/PrivacyPolicy';
import DocumentsPage from './pages/DocumentsPage';
import UploadDocsPage from './components/UploadDocsPage';
import ClassificationResultsPage from './components/ClassificationResultsPage';
import DocHistoryPage from './components/DocHistoryPage';
import DocumentClassificationList from './pages/DocumentClassificationList';
import AssistantPage from './pages/AssistantPage';
import Forbidden from './pages/Forbidden';
import Connections from './pages/Connections';
import Mappings from './pages/Mappings';
import MappingFields from './pages/MappingFields';
import MappingNew from './pages/MappingNew';
import './App.css';
import WorkflowList from './components/WorkflowList';
import Events from './components/Events';
import ConnectionForm from './components/connection/ConnectionForm';
import ActionForm from './components/connection/ActionForm';
import WorkflowEditor from './components/WorkflowEditor';
import { AppSnackbarProvider } from './contexts/AppSnackbarContext';
import { AppConfirmProvider } from './contexts/AppConfirmContext';
import WorkflowAdmin from './pages/WorkflowAdmin';
import WorkflowDashboard from './pages/WorkflowDashboard';
import WorkflowTrigger from './pages/WorkflowTrigger';
import RuleSetList from './components/RuleSetList';
import SourcesList from './components/SourcesList';
import RuleSet from './components/RuleSet';
import Tasks from './pages/Tasks';

function App() {

    const [warnOpen, setWarnOpen] = useState(false);
    const [secondsLeft, setSecondsLeft] = useState(0);

    // 1) Global 401 -> redirect to login
    useEffect(() => {
        const onExpired = () => {
            setWarnOpen(false);
            window.location.href = "/login?reason=expired";
        };
        window.addEventListener("session-expired", onExpired);
        return () => window.removeEventListener("session-expired", onExpired);
    }, []);

    // 2) Idle timer (warn before timeout & enforce timeout)
    const onWarn = useCallback((secs) => {
        setSecondsLeft(secs);
        setWarnOpen(true);
    }, []);

    const onTimeout = useCallback(async () => {
        // Server sign-out for cleanliness (idempotent)
        try { await apiFetch("/api/account/logout", { method: "POST" }); } catch { }
        window.location.href = "/login?reason=expired";
    }, []);

    useIdleTimeout({
        idleMinutes: 20,        // must match Program.cs options.ExpireTimeSpan
        warnBeforeSeconds: 120,
        onWarn,
        onTimeout,
    });

    // 3) �Stay signed in� (refresh sliding expiration)
    const staySignedIn = async () => {
        try { await apiFetch("/api/account/ping"); } catch { } // 401 -> global handler takes over
        setWarnOpen(false);
    };

    // 4) Explicit sign out
    const signOutNow = async () => {
        try { await apiFetch("/api/account/logout", { method: "POST" }); } catch { }
        window.location.href = "/login";
    };


    // Fire a best?effort logout when the tab is closed / backgrounded.
    // you can’t reliably tell “tab closed” vs “page refreshed” from the page itself. Both trigger the same unload lifecycle,
    //  unless we find a way to handle it from the server (e.g. track active sessions with a heartbeat, and expire shortly after missing heartbeats).
    /* useEffect(() => {
        const sendBeaconLogout = () => {
            try {
                // Use POST + JSON body; sendBeacon requires a Blob/ArrayBuffer/FormData
                const url = "/api/account/logout-beacon";
                const body = new Blob([JSON.stringify({})], { type: "application/json" });
                // Best-effort: harmless if the user is not logged in
                navigator.sendBeacon?.(url, body);
            } catch {
                // ignore
            }
        };

        // pagehide handles back/forward cache & iOS Safari better than beforeunload
        window.addEventListener("pagehide", sendBeaconLogout);

        return () => {
            window.removeEventListener("pagehide", sendBeaconLogout);
        };
    }, []); */

    return (
        <>
        <AuthProvider>
            <AppSnackbarProvider>
                <AppConfirmProvider>
                    <Routes>
                        <Route path="/login" element={<Login />} />
                        <Route path="/forbidden" element={<Forbidden />} />
                        <Route element={<ProtectedRoute><BaseLayout /></ProtectedRoute>}>
                            <Route path="/" element={<Home />} />
                            <Route path="/documents" element={<DocumentsPage />} />
                            <Route path="/changePassword" element={<ChangePassword />} />
                            <Route path="/testing" element={<FileUpload />} />
                            <Route path="/training" element={<TrainingSetList />} />
                            <Route path="/training/:id" element={<TrainingSetDetail />} />
                            <Route path="/jobs" element={<JobDashboard />} />
                            <Route path="/jobs/:jobId" element={<JobDetail />} />
                            <Route path="/documentmodels" element={<DocumentModels />} />
                            <Route path="/notifications" element={<NotificationPage />} />
                            <Route path="/account" element={<Account />} />
                            <Route path="/upload-docs" element={<UploadDocsPage />} />
                            <Route path="/classification-results/:id" element={<ClassificationResultsPage />} />
                            <Route path="/doc-history" element={<DocHistoryPage />} />
                            <Route path="/document-classifications" element={<DocumentClassificationList />} />
                            <Route path="/assistant" element={<AssistantPage />} />
                            <Route path="/security" element={<SecurityAdminLayout />}>
                                <Route index element={<SecurityUsers />} />
                                <Route path="users" element={<SecurityUsers />} />
                                <Route path="users/:id" element={<SecurityUserDetail />} />
                                <Route path="groups" element={<SecurityGroups />} />
                                <Route path="groups/:id" element={<SecurityGroupDetail />} />
                                <Route path="roles" element={<SecurityRoles />} />
                                <Route path="roles/:id" element={<SecurityRoleDetail />} />
                                <Route path="features" element={<SecurityFeatures />} />
                                <Route path="settings" element={<SecuritySettings />} />
                            </Route>
                            <Route path="/workflow" element={<WorkflowDashboard />} />
                            <Route path="/workflow/editor" element={<WorkflowEditor />} />
                            <Route path="/workflow/editor/:workflowId" element={<WorkflowEditor />} />
                            <Route path="/workflow/editorlist" element={<WorkflowList />} />
                            <Route path="/workflow/admin" element={<WorkflowAdmin />} />
                            <Route path="/workflow/trigger/:id" element={<WorkflowTrigger />} />
                            <Route path="/workflow/rules" element={<RuleSetList />} />
                            <Route path="/workflow/rules/:ruleSetId" element={<RuleSet />} />
                            <Route path="/workflow/rules/:ruleSetId/sources" element={<SourcesList />} />

                            <Route path="/terms" element={<TermsOfUse />} />
                            <Route path="/privacy" element={<PrivacyPolicy />} />

                            <Route path="/mappings" element={<Mappings />} >
                                <Route path=":id" element={<MappingFields />} />
                                <Route path="new" element={<MappingNew />} />
                            </Route>

                            <Route path="/connections" element={<Connections />} >
                                <Route path="new" element={<ConnectionForm />} />
                                <Route path=":connectionId/edit" element={<ConnectionForm />} />
                                <Route path=":connectionId/actions/new" element={<ActionForm />} />
                                <Route path=":connectionId/actions/:actionId/edit" element={<ActionForm />} />
                                <Route path=":conId/events/:eventId" element={<Events />} />
                            </Route>
                            <Route path="/tasks" element={<Tasks />} />
                        </Route>
                    </Routes>
                </AppConfirmProvider>
            </AppSnackbarProvider>
        </AuthProvider>
        <SessionTimeoutDialog
                open={warnOpen}
                secondsLeft={secondsLeft}
                onStay={staySignedIn}
                onSignOut={signOutNow}
            />
        </>
    );
}

export default App;
